import { CircleFilled, XIcon } from '@commenda/icons';
import { Icons } from '@commenda/kakashi';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { IncorporationRequestDataType } from '@commenda/types';
import { cn } from '@commenda/utils';
import { useState } from 'react';
import { KeyedMutator } from 'swr';
import { getDirectorName } from '../Director/common';
import {
  BaseDirectorType,
  DirectorFormType,
} from '../Director/DirectorFormType';
import DirectorModal from '../Director/new/DirectorModal';
import { CorporationForEmailValidation } from '../../common/helpers';

const DirectorCardKS = <DirectorType extends BaseDirectorType>({
  companyDataMutate,
  director,
  corporationId,
  selectDirectorById,
  setNotification,
  companyId,
  token,
  onErrorResolve,
  hasError,
  onRemoveDirector,
  directorForm,
  allCorporations,
  modalConfigOverride,
  workRequestData,
}: {
  companyDataMutate: KeyedMutator<unknown>;
  director: DirectorBase;
  corporationId?: number;
  selectDirectorById?: (directorId: number) => void;
  setNotification: TSetNotification;
  companyId: number;
  token: string;
  onErrorResolve: () => void;
  hasError: boolean;
  onRemoveDirector: (directorId: number) => void;
  directorForm: DirectorFormType<DirectorBase>;
  allCorporations?: CorporationForEmailValidation[];
  modalConfigOverride?: {
    heading?: string;
    subheading?: string;
    dialogTitle?: string;
  };
  workRequestData?: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
}) => {
  const [isEditDirectorModalShown, setEditDirectorModalShown] = useState(false);

  const directorName = getDirectorName(director);

  return (
    <div
      className={cn(
        'p-1.5 border-b border-primary',
        hasError ? 'border-error-primary' : ''
      )}
    >
      <div className="flex justify-between items-center">
        <div className="flex gap-2 items-center">
          {hasError ? (
            <CircleFilled />
          ) : (
            <Icons.User01 className="text-fg-quinary h-5 w-5" />
          )}
          <h4 className="text-text-primary txt-small-medium">{directorName}</h4>
        </div>
        <div className="flex gap-1 items-center">
          <div className="p-2 hover:bg-bg-secondary-hover rounded-sm">
            <Icons.Pencil02
              onClick={() => setEditDirectorModalShown(true)}
              className="h-5 w-5 text-button-tertiary-fg hover:text-button-tertiary-fg-hover cursor-pointer"
            />
          </div>
          <div className="p-2 hover:bg-bg-secondary-hover rounded-sm">
            <XIcon
              className="h-5 w-5 cursor-pointer text-button-tertiary-fg hover:text-button-tertiary-fg-hover"
              onClick={() => onRemoveDirector(director.id)}
            />
          </div>
        </div>

        {isEditDirectorModalShown ? (
          <DirectorModal
            isOpen={isEditDirectorModalShown}
            setOpen={setEditDirectorModalShown}
            companyDataMutate={companyDataMutate}
            DirectorForm={directorForm}
            setNotification={setNotification}
            companyId={companyId}
            corporationId={corporationId}
            token={token}
            director={director}
            modalConfig={{
              isDirector: true,
              heading:
                modalConfigOverride?.heading || 'Edit director information',
              subheading:
                modalConfigOverride?.subheading ||
                'Edit details below to update director',
              dialogTitle: modalConfigOverride?.dialogTitle,
              postSave: () => onErrorResolve(),
            }}
            allCorporations={allCorporations}
            workRequestData={workRequestData}
          />
        ) : null}
      </div>
      {hasError ? (
        <p className="ml-4 text-text-error">*missing details</p>
      ) : null}
    </div>
  );
};

export default DirectorCardKS;
